import type { FC } from "react";
import { useEffect, useMemo, useState } from "react";

import { LongArrowForwardIcon } from "@jti/brand-icons/long-arrow-forward";
import { Button } from "@jti/core-components/components/Button";
import { AUTH_ROUTE_PATHS } from "@jti/fe-common/routes/auth-routes";
import { getUser, IrsApi, LoyaltyApi } from "@jti/rest-api";
import { useDispatch, useSelector } from "@jti/store/reduxHooks";
import { fetchUserPoints } from "@jti/store/slices/loyaltyCoreSlice";
import { type RootStoreState } from "@jti/store/types";
import { ComponentHeading } from "@jti/ui/components/ComponentHeading";
import { Hero as HeroLib } from "@jti/ui/components/Hero";
import { Typography } from "@jti/ui/components/Typography";
import { RichText } from "@jti/ui/eva-components/RichText";
import { useAuthHrefResolver } from "@jti/ui/providers/AuthRoutingProvider";
import { replaceStringPlaceholder } from "@jti/ui/utils/replaceStringPlaceholder";

import useLoggedUser from "../../hooks/useLoggedUser";
import { trackCtaClick } from "../../utils/trackCtaClick";
import type { AccountActionButtonsType } from "../AccountActionButtons/AccountActionButtons";
import { AccountActionButtons } from "../AccountActionButtons/AccountActionButtons";

import { PackCodeDialog } from "./PackCodeDialog/PackCodeDialog";
import { PackCodeInputSection } from "./PackCodeInputSection/PackCodeInputSection";

import styles from "./PackCodes.module.scss";

export type PackCodesProps = {
  title: string;
  background: string;
  mobileHeaderImage: string;
  mobileBottomImage: string;
  loggedInDescription: string;
  loggedOutDescription: string;
  limitText: string;
  limitTextReached: string;
  inputPlaceholder: string;
  buttonTitle: string;
  confirmationTitle: string;
  errorCodeMessage: string;
  errorEarningMessage: string;
  bottomMessageHistory: string;
  bottomMessageHistoryLink: string;
  packCodeName: string;
  packCodeText: string;
  packCodeLink?: string;
  earningMessage: string;
  headerLogo: string;
  componentTitle: string;
  closeButtonText?: string;
  restrictedContent?: boolean;
  restrictedTitle: string;
  restrictedDescription: string;
  restrictedCta: string;
  restrictedCtaUrl: string;
} & AccountActionButtonsType;

type IrsState = {
  isQueryInProgress: boolean;
  hasCodeError: boolean;
  hasEarningError: boolean;
  isValid: boolean;
  limitReached: boolean;
  showConfirmationDialog: boolean;
  earningRulePoints: number | string;
  errorCode?: string;
};

export const PackCodes: FC<PackCodesProps> = ({
  signInTitle,
  registerTitle,
  signInUrl,
  registerUrl,
  title,
  loggedInDescription,
  loggedOutDescription,
  limitText,
  limitTextReached,
  inputPlaceholder,
  buttonTitle,
  confirmationTitle,
  errorCodeMessage,
  errorEarningMessage,
  background,
  mobileHeaderImage,
  bottomMessageHistory,
  bottomMessageHistoryLink,
  packCodeLink,
  packCodeName,
  packCodeText,
  earningMessage,
  headerLogo,
  mobileBottomImage,
  componentTitle = "PACK CODES",
  closeButtonText = "Close",
  restrictedTitle,
  restrictedDescription,
  restrictedCta,
  restrictedCtaUrl,
  restrictedContent,
}) => {
  const resolveAuthHref = useAuthHrefResolver();
  const isRestricted = useSelector((state: RootStoreState) => state.loyaltyCore.isRestricted);
  const showRestrictedContent = isRestricted && restrictedContent;
  const [isLogged, __, isLoaded] = useLoggedUser();
  const dispatch = useDispatch();
  const [inputPackCode, setInputPackCode] = useState<string>("");
  const [codeRegistrationStatus, setCodeRegistrationStatus] = useState<IrsState>({
    isQueryInProgress: false,
    hasCodeError: false,
    hasEarningError: false,
    isValid: false,
    limitReached: false,
    showConfirmationDialog: false,
    earningRulePoints: 0,
  });

  const registerPackCode = async (code: string) => {
    setCodeRegistrationStatus((prev) => ({ ...prev, isQueryInProgress: true }));

    const packCodeResponse = await IrsApi.init().registerPackCode(code);
    setCodeRegistrationStatus((prev) => ({
      ...prev,
      hasCodeError: packCodeResponse.hasError,
      isValid: packCodeResponse.isValid,
      limitReached: packCodeResponse.limitReached,
      errorCode: packCodeResponse.errorCode,
    }));

    if (packCodeResponse?.loyalty_rule) {
      try {
        const user = getUser();
        if (user?.uuid) {
          const earningRulePointsResponse = await LoyaltyApi.init().getEarningRulePoints(
            packCodeResponse.loyalty_rule,
            user.ciam_token,
            user.uuid
          );
          setCodeRegistrationStatus((prev) => ({
            ...prev,
            earningRulePoints: earningRulePointsResponse,
            showConfirmationDialog: packCodeResponse.isValid && earningRulePointsResponse > 0,
          }));
          setInputPackCode("");
          dispatch(
            fetchUserPoints({
              useLocalCache: false,
              dispatchUpdateEvent: true,
              dispatchCodeRegistrationEvent: true,
            })
          );
        } else {
          console.warn("User UUID is missing. Cannot fetch earning rule points.");
        }
      } catch {
        setCodeRegistrationStatus((prev) => ({ ...prev, hasEarningError: true }));
      }
    }
    setCodeRegistrationStatus((prev) => ({ ...prev, isQueryInProgress: false }));
  };

  useEffect(() => {
    if (!isLoaded) return;

    const queryParams = new URLSearchParams(window.location.search);
    const packCode = queryParams.get("packCode");

    if (packCode && isLogged) {
      setInputPackCode(packCode);
    }

    if (packCode && !isLogged) {
      window.location.href = resolveAuthHref({
        route: AUTH_ROUTE_PATHS.login,
        search: { returnUrl: window.location.href },
      });
    }
  }, [isLogged, isLoaded, resolveAuthHref]);

  const handleClick = () => {
    if (!inputPackCode) return;
    registerPackCode(inputPackCode);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputPackCode(event.target.value);
  };

  const earnedPointsMessage = useMemo(() => {
    const points = Number(codeRegistrationStatus.earningRulePoints);
    if (!points || points === 0) return "";

    return replaceStringPlaceholder(
      earningMessage,
      [
        {
          placeholder: "points",
          value: codeRegistrationStatus.earningRulePoints.toString(),
        },
      ],
      false
    );
  }, [earningMessage, codeRegistrationStatus.earningRulePoints]);

  return (
    <div className={styles.packCodes}>
      <ComponentHeading HtmlElement={"h1"}>{componentTitle}</ComponentHeading>
      <PackCodeDialog
        showConfirmationDialog={codeRegistrationStatus.showConfirmationDialog}
        onClose={() => setCodeRegistrationStatus((prev) => ({ ...prev, showConfirmationDialog: false }))}
        confirmationTitle={confirmationTitle}
        earningMessage={earnedPointsMessage}
        historyMessage={bottomMessageHistory}
        historyMessageLink={bottomMessageHistoryLink}
        limitTextReached={limitTextReached}
        limitReached={codeRegistrationStatus.limitReached}
        closeButtonText={closeButtonText}
      />
      <HeroLib
        img={background}
        mobileImg={mobileHeaderImage}
        bottomMobileImg={mobileBottomImage}
        isLogged={isLogged}
        fullscreen={false}
        containerClassName={styles.packCodesHeroContainer}
        bannerContainerClassName={styles.packCodesHeroBannerContainer}
        headerContent={<img src={headerLogo} alt="logo" className={styles.logo} />}
        loggedTitle={
          <Typography variant="h2" weight="light" className={styles.title} dataTestId="packCodeLoggedInTitle">
            {showRestrictedContent ? restrictedTitle : title}
          </Typography>
        }
        unloggedTitle={
          <Typography variant="h2" weight="light" className={styles.title} dataTestId="packCodeUnloggedInTitle">
            {title}
          </Typography>
        }
        loggedContent={
          <>
            {showRestrictedContent ? (
              <Typography
                variant="body2"
                weight="light"
                className={styles.description}
                dataTestId="packCodeRestrictedDescription"
              >
                {restrictedDescription}
              </Typography>
            ) : (
              <RichText
                content={loggedInDescription}
                className={`${styles.description} ${styles.richTextDescription}`}
                dataTestId="packCodeLoggedInDescription"
              />
            )}
            {showRestrictedContent ? (
              <div className={styles.craWrapper}>
                <Button
                  href={restrictedCtaUrl}
                  variant="text-muted"
                  size="sm"
                  iconAfter={LongArrowForwardIcon}
                  data-testid="restrictedCtaButton"
                  onClick={() => trackCtaClick(restrictedCta, "LoyaltyPackCodesRestrictedCta")}
                >
                  {restrictedCta}
                </Button>
              </div>
            ) : (
              <>
                <p className={styles.limitText}>{limitText}</p>
                <section className={styles.packCodeWrapper}>
                  <p className={styles.linkText}>{packCodeName}</p>
                  {packCodeLink ? (
                    <a href={packCodeLink} className={styles.link}>
                      {packCodeText}
                    </a>
                  ) : (
                    <p>{packCodeText}</p>
                  )}
                </section>
                <PackCodeInputSection
                  inputPackCode={inputPackCode}
                  handleInputChange={handleInputChange}
                  handleClick={handleClick}
                  hasCodeError={codeRegistrationStatus.hasCodeError}
                  hasEarningError={codeRegistrationStatus.hasEarningError}
                  isValid={codeRegistrationStatus.isValid}
                  limitReached={codeRegistrationStatus.limitReached}
                  isQueryInProgress={codeRegistrationStatus.isQueryInProgress}
                  inputPlaceholder={inputPlaceholder}
                  earnedPointsMessage={earnedPointsMessage}
                  errorCodeMessage={errorCodeMessage}
                  errorEarningMessage={errorEarningMessage}
                  limitTextReached={limitTextReached}
                  buttonTitle={buttonTitle}
                  errorCode={codeRegistrationStatus.errorCode}
                />
                <a href={bottomMessageHistoryLink} className={styles.historyInfo}>
                  {bottomMessageHistory}
                </a>
              </>
            )}
          </>
        }
        unloggedContent={
          <>
            <RichText
              content={loggedOutDescription}
              className={`${styles.description} ${styles.richTextDescription}`}
              dataTestId="packCodeLoggedOutDescription"
            />
            <div className={styles.ctaWrapper}>
              <AccountActionButtons
                signInTitle={signInTitle}
                registerTitle={registerTitle}
                signInUrl={signInUrl}
                registerUrl={registerUrl}
                position="start"
                theme="dark"
              />
            </div>
          </>
        }
      ></HeroLib>
    </div>
  );
};
